/**
 * ImageManager - Manages raster image entities in the drawing.
 *
 * Creates OdDbRasterImage entities from image data (JPG, PNG).
 * Images are stored in the database's image dictionary and can be
 * placed in model space.
 *
 * Images are accesed as external files. There is a way to put them inside the drawing through OLE objects. here is an example of that :
 * https://docs.opendesign.com/td/td_insert_OLE_objects.html
 * But the class are not available in DrawingWeb.js today
 *
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode, DictIterType } from "../core/constants";
import { AddEntityCommand } from "../commands/AddEntityCommand";
import { selectionService } from "../selection";
import { LoadingTimer } from "@/utils/LoadingTimer";
import { TransformEntityCommand } from "../commands/TransformEntityCommand";
import {
  getTransformableEntityType,
  TransformableEntityType,
} from "../entities/detection";
import { readEntityBbox } from "../meta/extents";
import {
  applyTransformToEntityIds,
  rotationTransform,
} from "../transform/entityTransform";

/** Layer name for images */
export const IMAGE_LAYER_NAME = "DR_Images";

export class ImageManager {
  constructor(private viewer: DRDrawingViewer) {}

  /**
   * Load referenced images into the Emscripten virtual filesystem.
   * This is used when opening drawings that have external raster images.
   * The paths are stored in the DWG file, but the actual image data
   * needs to be provided separately (e.g., from IndexedDB).
   *
   * @param images Array of { path: string, array: Uint8Array } objects
   */
  loadReferencedImages(images: { path: string; array: Uint8Array }[]): void {
    const FS = (window as any).FS;
    if (!FS) {
      console.error("[ImageManager] Emscripten FS not available");
      return;
    }

    for (const image of images) {
      try {
        // Ensure the directory exists
        const dir = image.path.substring(0, image.path.lastIndexOf("/"));
        if (dir) {
          try {
            FS.mkdirTree(dir);
          } catch {
            // Directory may already exist
          }
        }

        // Write image to virtual filesystem
        FS.writeFile(image.path, image.array);
      } catch (error) {
        console.error(
          "[ImageManager] Failed to load referenced image:",
          image.path,
          error,
        );
      }
    }
  }

  /**
   * Reload all OdDbRasterImageDef objects in the database.
   * After opening a DWG, ODA only loads image metadata (paths, dimensions).
   * Calling load() on each definition tells ODA to read the actual pixel
   * data from the virtual filesystem so the renderer can display images
   * instead of showing a rectangle with the file path.
   */
  reloadImageDefs(): void {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) return;

    const imageDictId = module.OdDbRasterImageDef.imageDictionary(db);
    if (imageDictId.isNull()) return;

    const imageDict = openAs(
      imageDictId,
      module.OdDbDictionary,
      OpenMode.kForRead,
    );
    const numImages = imageDict.numEntries();
    if (numImages === 0) {
      deleteObj(imageDict);
      return;
    }

    LoadingTimer.step(`reloadImageDefs (${numImages} images)`);

    const iterator = imageDict.newIterator(DictIterType.kDictCollated as any);
    let imageIndex = 0;

    while (!iterator.done()) {
      try {
        const obj = iterator.getObject(OpenMode.kForWrite);
        const imageDef = module.OdDbRasterImageDef.cast(obj);
        LoadingTimer.step(`imageDef.load[${imageIndex}]`);
        imageDef.load(false);
        deleteObj(imageDef);
        deleteObj(obj);
        imageIndex++;
      } catch (error) {
        console.warn("[ImageManager] Failed to reload image def:", error);
      }
      iterator.next();
    }

    deleteObj(iterator);
    deleteObj(imageDict);

    LoadingTimer.step("reloadImageDefs done");
  }

  /**
   * Add an image centered in the current view.
   * The image is scaled to fit within the view (with 80% margin).
   * @param filename Original filename (for reference name)
   * @param data Image data as Uint8Array
   * @returns The raster path on success, null on failure
   */
  add(
    filename: string,
    data: Uint8Array,
    offsetIndex: number = 0,
  ): string | null {
    const result = this.addCenteredInView(filename, data, offsetIndex);
    if (result) {
      this.viewer.update();
      this.viewer.app.regenAll();
      return `/assets/${filename}`;
    }
    return null;
  }

  /**
   * Insert an image at a specific position.
   * @param filename Original filename
   * @param data Image data as Uint8Array
   * @param origin Position (bottom-left corner) in world coordinates
   * @param width Width in world units
   * @param height Height in world units
   * @returns The entity ID, or null on failure
   */
  insertAt(
    filename: string,
    data: Uint8Array,
    origin: { x: number; y: number; z: number },
    width: number,
    height: number,
  ): DrawingModule.OdDbObjectId | null {
    const imageDefId = this.createImageDef(filename, data);
    if (!imageDefId) return null;

    const entityId = this.insertImage(imageDefId, origin, width, height);
    if (entityId) {
      const command = new AddEntityCommand(this.viewer, {
        entityHandle: getEntityHandle(entityId),
        originalLayerName: IMAGE_LAYER_NAME,
      });
      this.viewer.executeCommand(command);
      this.viewer.update();
      this.viewer.app.regenAll();
    }
    return entityId;
  }

  // ==================== Private Methods ====================

  /**
   * Ensure the image layer exists in the database.
   */
  private ensureImageLayerExists(): void {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) return;

    const layerTableId = db.getLayerTableId();
    const layerTable = openAs(
      layerTableId,
      module.OdDbLayerTable,
      OpenMode.kForWrite,
    );

    const layerNameStr = new module.OdString();
    layerNameStr.set(IMAGE_LAYER_NAME);

    const exists = layerTable.has1(layerNameStr);

    if (!exists) {
      const imageLayer = module.OdDbLayerTableRecord.createObject();
      imageLayer.setName(layerNameStr);
      layerTable.add(imageLayer);
      deleteObj(imageLayer);
    }

    deleteObj(layerNameStr);
    deleteObj(layerTable);
  }

  /**
   * Get the layer ID for the image layer.
   */
  private getImageLayerId(): DrawingModule.OdDbObjectId | null {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) return null;

    const layerTableId = db.getLayerTableId();
    const layerTable = openAs(
      layerTableId,
      module.OdDbLayerTable,
      OpenMode.kForRead,
    );

    const layerNameStr = new module.OdString();
    layerNameStr.set(IMAGE_LAYER_NAME);

    let layerId: DrawingModule.OdDbObjectId | null = null;

    if (layerTable.has1(layerNameStr)) {
      layerId = layerTable.getAt3(layerNameStr, false);
    }

    deleteObj(layerNameStr);
    deleteObj(layerTable);

    return layerId;
  }

  /**
   * Create an image definition from image data.
   * The image is written to the virtual filesystem and loaded.
   */
  private createImageDef(
    filename: string,
    imageData: Uint8Array,
  ): DrawingModule.OdDbObjectId | null {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) {
      console.error("[ImageManager] No database available");
      return null;
    }

    try {
      // Use global FS object (Emscripten)
      const FS = (window as any).FS;
      if (!FS) {
        console.error("[ImageManager] Emscripten FS not available");
        return null;
      }

      // Ensure assets directory exists in virtual filesystem
      try {
        FS.mkdir("/assets");
      } catch {
        // Directory may already exist, ignore error
      }

      // Write image to virtual filesystem
      const filePath = `/assets/${filename}`;
      FS.writeFile(filePath, imageData);

      // Get or create image dictionary
      let imageDictId = module.OdDbRasterImageDef.imageDictionary(db);
      if (imageDictId.isNull()) {
        imageDictId = module.OdDbRasterImageDef.createImageDictionary(db);
      }

      // Open dictionary for writing
      const imageDict = openAs(
        imageDictId,
        module.OdDbDictionary,
        OpenMode.kForWrite,
      );

      // Create image definition
      const imageDef = module.OdDbRasterImageDef.createObject();

      // Set source filename (use absolute path)
      const filePathStr = new module.OdString(filePath);
      imageDef.setSourceFileName(filePathStr);

      // Generate a unique name for the dictionary entry
      const baseName = filename.replace(/\.[^/.]+$/, ""); // Remove extension
      const nameStr = new module.OdString(baseName);

      // Add to dictionary FIRST (before loading)
      const imageDefId = imageDict.setAt(nameStr, imageDef);

      // Now try to load the image (after it's in the database)
      // Note: load() may not be supported in all WASM builds
      try {
        imageDef.load(true);
      } catch (loadError) {
        console.warn(
          "[ImageManager] Image load not supported, continuing without:",
          loadError,
        );
      }

      deleteObj(filePathStr);
      deleteObj(nameStr);
      deleteObj(imageDef);
      deleteObj(imageDict);

      return imageDefId;
    } catch (error) {
      console.error("[ImageManager] Failed to create image definition:", error);
      return null;
    }
  }

  /**
   * Insert an image entity into model space.
   */
  private insertImage(
    imageDefId: DrawingModule.OdDbObjectId,
    origin: { x: number; y: number; z: number },
    width: number,
    height: number,
  ): DrawingModule.OdDbObjectId | null {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) {
      console.error("[ImageManager] No database available");
      return null;
    }

    try {
      // Ensure image layer exists
      this.ensureImageLayerExists();
      const layerId = this.getImageLayerId();

      // Create raster image entity
      const image = module.OdDbRasterImage.createObject();
      image.setDatabaseDefaults(db, true);

      // Set layer
      if (layerId) {
        image.setLayer1(layerId, false, false);
      }

      // Link to image definition
      image.setImageDefId(imageDefId);

      // Set orientation: origin, u vector (horizontal), v vector (vertical)
      const originPt = new module.OdGePoint3d(origin.x, origin.y, origin.z);
      const uVector = new module.OdGeVector3d(width, 0, 0);
      const vVector = new module.OdGeVector3d(0, height, 0);

      image.setOrientation(originPt, uVector, vVector);

      deleteObj(originPt);
      deleteObj(uVector);
      deleteObj(vVector);

      // Append to current space
      const currentSpaceId = getCurrentSpaceId(db);
      const currentSpace = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const entityId = currentSpace.appendOdDbEntity(image);

      // Close block and image before accessing sortents table
      // (following GeoCommands.cpp pattern: entity must be closed first)
      deleteObj(currentSpace);
      deleteObj(image);

      // Push image to bottom of draw order so shapes remain visible on top
      const msForSort = openAs(
        currentSpaceId,
        module.OdDbBlockTableRecord,
        OpenMode.kForWrite,
      );
      const sortentsTable = msForSort.getSortentsTable(true);
      const idArray = new module.OdDbObjectIdArray();
      idArray.push_back(entityId);
      sortentsTable.moveToBottom(idArray);
      deleteObj(idArray);
      deleteObj(sortentsTable);
      deleteObj(msForSort);

      return entityId;
    } catch (error) {
      console.error("[ImageManager] Failed to insert image:", error);
      return null;
    }
  }

  // ============== Rotate Selected ==============

  /** Une seule entité sélectionnée, et c'est une image raster. */
  canRotateSelected(): boolean {
    const ids = selectionService.getAll();
    return (
      ids.length === 1 &&
      getTransformableEntityType(this.viewer.module, ids[0]) ===
        TransformableEntityType.RasterImage
    );
  }

  /**
   * Fait pivoter l'image sélectionnée sur place, autour du centre de son emprise
   * (sens trigonométrique pour un angle positif). Annulable : même contrat que
   * les poignées — la transformation est appliquée ici, la commande ne sert
   * qu'à rejouer l'inverse.
   */
  rotateSelected(angleDegrees: number): boolean {
    if (!this.canRotateSelected()) return false;
    const module = this.viewer.module;
    const entityId = selectionService.getAll()[0];

    const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForRead);
    const bbox = readEntityBbox(module, entity);
    deleteObj(entity);
    if (!bbox) return false;

    const center = {
      x: (bbox.minX + bbox.maxX) / 2,
      y: (bbox.minY + bbox.maxY) / 2,
      z: 0,
    };
    const transform = rotationTransform((angleDegrees * Math.PI) / 180, center);
    applyTransformToEntityIds(module, [entityId], transform);
    this.viewer.executeCommand(
      new TransformEntityCommand(this.viewer, [
        { handle: getEntityHandle(entityId), transform },
      ]),
    );
    this.viewer.update();
    selectionService.updateGrid();
    return true;
  }

  /**
   * Add an image centered in the current view.
   */
  private addCenteredInView(
    filename: string,
    imageData: Uint8Array,
    offsetIndex: number = 0,
  ): DrawingModule.OdDbObjectId | null {
    const module = this.viewer.module;

    // Create image definition
    const imageDefId = this.createImageDef(filename, imageData);
    if (!imageDefId) {
      return null;
    }

    try {
      // Try to get image pixel dimensions from definition
      let imageWidth = 0;
      let imageHeight = 0;

      try {
        const imageDef = openAs(
          imageDefId,
          module.OdDbRasterImageDef,
          OpenMode.kForRead,
        );
        const imageSize = imageDef.size();
        imageWidth = imageSize.x;
        imageHeight = imageSize.y;
        deleteObj(imageSize);
        deleteObj(imageDef);
      } catch (sizeError) {
        console.warn(
          "[ImageManager] Could not get image size from def:",
          sizeError,
        );
      }

      // If dimensions not available, use 1:1 aspect ratio as fallback
      if (imageWidth <= 0 || imageHeight <= 0) {
        console.warn("[ImageManager] Using default 1:1 aspect ratio");
        imageWidth = 1;
        imageHeight = 1;
      }

      const aspectRatio = imageWidth / imageHeight;

      // Get current view bounds in world coordinates
      const canvas = this.viewer.canvas;
      const viewMin = this.viewer.screenToWorld({ x: 0, y: canvas.height });
      const viewMax = this.viewer.screenToWorld({ x: canvas.width, y: 0 });
      const viewWidth = Math.abs(viewMax.x - viewMin.x);
      const viewHeight = Math.abs(viewMax.y - viewMin.y);

      // Calculate size to fit within 80% of view
      const maxWidth = viewWidth * 0.8;
      const maxHeight = viewHeight * 0.8;

      let worldWidth: number;
      let worldHeight: number;

      if (maxWidth / aspectRatio <= maxHeight) {
        // Width is the limiting factor
        worldWidth = maxWidth;
        worldHeight = maxWidth / aspectRatio;
      } else {
        // Height is the limiting factor
        worldHeight = maxHeight;
        worldWidth = maxHeight * aspectRatio;
      }

      // Calculate center position (origin is bottom-left of image)
      const viewCenterX = (viewMin.x + viewMax.x) / 2;
      const viewCenterY = (viewMin.y + viewMax.y) / 2;
      const originX =
        viewCenterX - worldWidth / 2 + offsetIndex * worldWidth * 1.1;
      const originY = viewCenterY - worldHeight / 2;

      // Insert the image
      const entityId = this.insertImage(
        imageDefId,
        { x: originX, y: originY, z: 0 },
        worldWidth,
        worldHeight,
      );

      if (entityId) {
        // Execute AddEntityCommand for undo support
        const command = new AddEntityCommand(this.viewer, {
          entityHandle: getEntityHandle(entityId),
          originalLayerName: IMAGE_LAYER_NAME,
        });
        this.viewer.executeCommand(command);

        // Select the created image
        selectionService.clear();
        selectionService.add(entityId, true);
      }

      return entityId;
    } catch (error) {
      console.error(
        "[ImageManager] Failed to add image centered in view:",
        error,
      );
      return null;
    }
  }
}
